import { type Html, html, htmlList } from './html';
import { type ApiError, AuthError, CACHE, CAP, capNote, INFLIGHT, PAGES, dropSavedCopies, netBusy, sleep } from './api';
import { showLogin } from './auth';
import { App, S, type State, el, monthOf, nowMonth, setToday, today } from './base';
import { VT_OK, drawNav, monthNav, toast, vt, vtName } from './dom';
import { lists } from './lists';
import { labelHtml, monthLabel, morph, morphOk, sameLayout, slideLabel, snapshot } from './morph';

/* ================= Router ================= */
// The placeholder shows the shape of the page that's coming (KPI row, chart, list rows, table), so nothing jumps when it arrives
const SKELETONS: Record<string, string> = {
  '': 'dash', reports: 'dash', recap: 'dash', year: 'dash', timeline: 'dash', forecast: 'dash',
  transactions: 'feed', account: 'feed', review: 'feed', inbox: 'feed', map: 'chart',
  budgets: 'table', categories: 'table', bills: 'table', accounts: 'table', rules: 'table', recurring: 'table', loans: 'table', piggy: 'cards',
};
function skeleton() {
  const bar = (w: string, h: number, extra = '') => html`<div class="sk" style="width:${w};height:${h}px${extra}"></div>`;
  const rows = (n: number) => htmlList(Array.from({ length: n }, () => html`<div class="sk-row">${bar('34px', 34, ';border-radius:50%')}<div class="sk-grow">${bar('55%', 12)}<div style="height:8px"></div>${bar('30%', 10)}</div>${bar('64px', 12)}</div>`));
  const kpis = (n: number) => html`<div class="kpis sk-kpis">${htmlList(Array.from({ length: n }, () => html`<div class="kpi">${bar('48%', 10)}<div style="height:12px"></div>${bar('72%', 22)}</div>`))}</div>`;
  const card = (n: number) => html`<section class="panel">${bar('40%', 14)}<div style="height:18px"></div>${htmlList(Array.from({ length: n }, () => html`${bar('100%', 12)}<div style="height:16px"></div>`))}</section>`;
  const chart = html`<section class="panel">${bar('34%', 14)}<div style="height:16px"></div>${bar('100%', 180, ';border-radius:14px')}</section>`;
  const kind = SKELETONS[parseHash().name] || 'cards';
  const head = html`<div class="head"><div>${bar('200px', 26)}<div style="height:8px"></div>${bar('280px', 12)}</div></div>`;
  if (kind === 'dash') return html`${head}${kpis(4)}<div class="cards">${chart}${card(4)}</div>`;
  if (kind === 'feed') return html`${head}${kpis(3)}<section class="panel flush sk-list">${rows(7)}</section>`;
  if (kind === 'table') return html`${head}${kpis(3)}<section class="panel flush sk-list">${rows(6)}</section>`;
  if (kind === 'chart') return html`${head}${chart}`;
  return html`${head}<div class="cards">${card(5)}${card(3)}${card(4)}</div>`;
}
export function parseHash() {
  const h = location.hash.replace(/^#\/?/, '');
  const [p, q = ''] = h.split('?');
  const parts = p.split('/');
  return { name: parts[0] || '', id: parts[1] ? decodeURIComponent(parts[1]) : '', q: Object.fromEntries(new URLSearchParams(q)) };
}
export function hashWith(changes: Record<string, string | null | undefined>) {
  const r = parseHash(), q: Record<string, string | null | undefined> = { ...r.q, ...changes };
  Object.keys(q).forEach(k => (q[k] === '' || q[k] == null) && delete q[k]);
  const qs = new URLSearchParams(q as Record<string, string>).toString();
  return '#/' + r.name + (r.id ? '/' + encodeURIComponent(r.id) : '') + (qs ? '?' + qs : '');
}
// Each page module registers its view here by route name. A view draws the page by calling paint with its markup.
export type RouteInfo = ReturnType<typeof parseHash> & { at?: [number, number] };     // at: see viewYM
export type Paint = ((markup: Html | string) => void) & { live?: boolean, month?: boolean };
export type View = (r: RouteInfo, paint: Paint) => Promise<unknown> | void;
export const VIEWS: Record<string, View> = {};

/* ---------- Page cache: tabs you've opened come back exactly as you left them ---------- */
const CACHEABLE = new Set(['', 'transactions', 'budgets', 'accounts', 'account', 'categories', 'bills', 'piggy', 'forecast', 'reports', 'recurring', 'loans', 'recap', 'timeline']);
function stashPage(feed: State['feed']) {
  const v = el('view');
  unstepLabel(v);
  if (!S.pageLive || !S.pageKey || !S.pageCacheable || !v.firstChild) return;
  const scroll = scrollY, frag = document.createDocumentFragment();
  while (v.firstChild) frag.appendChild(v.firstChild);
  PAGES.delete(S.pageKey);
  PAGES.set(S.pageKey, { frag, scroll, feed, html: S.pageHtml, chart: S.chartData });
  while (PAGES.size > 12) PAGES.delete(PAGES.keys().next().value);
  S.pageLive = false;
}
// Keep "today" current when the app stays open past midnight; if you were on the current month, follow it.
function syncToday() {
  const now = new Date();
  if (now.getFullYear() === today.getFullYear() && now.getMonth() === today.getMonth() && now.getDate() === today.getDate()) return;
  const was = monthOf(today);
  if (S.y === was[0] && S.m === was[1]) [S.y, S.m] = monthOf(now);
  setToday(now); S.freshAfter = Date.now(); PAGES.clear();
}
// The month picker on screen shows the month just picked, its name sliding the way you went (the page's data
// follows when it comes; its own picker then already says the same, so it doesn't slide twice)
let stepped: { s: HTMLElement, html: string, title: string, next: HTMLButtonElement | null, off: boolean, today?: [Element, Element] } | null = null;
function stepLabel(v: HTMLElement, dir: string) {
  const s = v.querySelector<HTMLElement>('.month > span'), was = monthLabel(v);
  if (!s || was == null) return;
  const next = v.querySelector<HTMLButtonElement>('.month button[aria-label="Next month"]');
  if (!stepped || stepped.s !== s) stepped = { s, html: was, title: s.title, next, off: !!next && next.disabled };
  const tpl = document.createElement('template'); tpl.innerHTML = String(monthNav());
  const now = tpl.content.querySelector('.month > span'); if (!now) return;
  s.querySelectorAll('.mghost').forEach(g => g.remove());
  s.innerHTML = now.innerHTML; s.title = now.getAttribute('title') || '';
  const nb = tpl.content.querySelector('button[aria-label="Next month"]');
  if (next && nb) next.disabled = nb.hasAttribute('disabled');
  // Today shows up (or steps aside) with it
  const today = v.querySelector('.month > .btn.small'), tb = tpl.content.querySelector('.month > .btn.small');
  if (today && tb && today.outerHTML !== tb.outerHTML && !today.contains(document.activeElement)) { today.replaceWith(tb); stepped.today = [stepped.today ? stepped.today[0] : today, tb]; }
  slideLabel(v, was, dir);
}
// A page put away keeps the month it shows: its picker goes back to that month's name. The name on screen (maybe
// still sliding) moves over to the page that replaces it (keepLabel), so a quick answer doesn't cut its slide short.
let liveLab: HTMLElement | null = null;
function unstepLabel(v: HTMLElement) {
  if (!stepped) return;
  const { s, html: h, title, next, off, today } = stepped; stepped = null;
  if (!v.contains(s)) return;
  if (today && v.contains(today[1])) today[1].replaceWith(today[0]);
  const old = s.cloneNode(false) as HTMLElement; old.classList.remove('msliding'); old.innerHTML = h; old.title = title;
  s.replaceWith(old); if (next) next.disabled = off;
  liveLab = s.classList.contains('msliding') ? s : null;
}
function keepLabel(v: HTMLElement) {
  const s = liveLab; liveLab = null;
  const n = s && v.querySelector<HTMLElement>('.month > span');
  if (s && n && s.classList.contains('msliding') && labelHtml(n) === labelHtml(s)) { s.title = n.title; n.replaceWith(s); }
}
const IN_PLACE = new Set(['', 'reports', 'forecast', 'budgets', 'categories', 'bills', 'recap']);
export async function route(opts: { nav?: boolean, quiet?: boolean, instant?: boolean } = {}) {
  syncToday();
  const r = parseHash(), tok = ++S.tok, v = el('view'), stepped = S.vtDir, startedAt = Date.now();
  // The same page with other options (a report's dates, an account's range, the forecast's horizon): its charts change
  // in place like a month change, when the layout stays the same (see paint below)
  const sameRoute = IN_PLACE.has(r.name) && !!S.pageKey && S.pageKey.startsWith(r.name + '/' + r.id + '?');
  S.swr = !!opts.nav;
  const key = r.name + '/' + r.id + '?' + new URLSearchParams(r.q) + '@' + (S.all ? 'all' : S.y + '-' + S.m);
  const cacheable = CACHEABLE.has(r.name) && !S.dashEdit;
  const prevFeed = S.feed;
  // Which way are we going? Deeper pages slide in (push), shallower slide back (pop).
  // Back/forward swipes inside the iPhone app were already animated by iOS, so those show instantly.
  let traversal = false;
  const hn = history.state && history.state.n;
  if (hn == null) { S.navN = (S.navN || 0) + 1; try { history.replaceState({ n: S.navN }, ''); } catch (e) { /* ignore */ } }
  else { traversal = hn !== S.curNav; S.navN = Math.max(S.navN || 0, hn); }
  S.curNav = (history.state && history.state.n) || S.navN;
  // (a Settings category is a level deeper on phones, where it opens from the list of them)
  const depth = ({ account: 1, reconcile: 2 })[r.name] || (r.name === 'settings' && r.q.s && innerWidth < 1100 ? 1 : 0);
  if (opts.nav && S.pageDepth != null && !S.vtDir && depth !== S.pageDepth) S.vtDir = depth > S.pageDepth ? 'push' : 'pop';
  S.pageDepth = depth;
  const instant = opts.instant || (traversal && !!window.MONEY_NATIVE);
  if (instant) S.vtDir = '';
  drawNav(r.name === 'account' || r.name === 'reconcile' ? 'accounts' : r.name === 'inbox' ? 'review' : r.name);
  clearTimeout(S.skelT);
  // A tab you've already opened: put it back instantly, no network, same scroll position.
  const saved = opts.nav && cacheable && key !== S.pageKey && PAGES.get(key);
  if (saved) {
    PAGES.delete(key);
    const dir = S.vtDir; S.vtDir = '';
    // another month of a page that keeps its layout: redraw in place from what's on screen (see paint below)
    const snap = (dir === 'next' || dir === 'prev' || (!dir && sameRoute)) && !instant && morphOk() && S.pageLive && v.firstElementChild ? snapshot(v) : null;
    const inPlace = !!snap && sameLayout(snap, saved.frag, dir);
    const swap = () => {
      const at = scrollY, lab = monthLabel(v);
      stashPage(S.feed);
      v.replaceChildren(saved.frag); keepLabel(v); if (!instant) slideLabel(v, lab, dir); v.classList.remove('enter', ...(inPlace ? ['anim'] : []));
      if (!instant && !inPlace && (!dir || !VT_OK)) { void v.offsetWidth; v.classList.add('enter'); }     // the same entrance as a first visit
      S.pageKey = key; S.pageHtml = saved.html; S.pageCacheable = true; S.pageLive = true;
      S.feed = saved.feed; S.kIdx = -1; if (saved.chart) S.chartData = saved.chart;
      scrollTo(0, inPlace ? at : saved.scroll);
      if (inPlace) morph(v, snap!, dir);
      else if (window.App && App.replayIn) App.replayIn();
      if (window.App && App.fillStrip) App.fillStrip();     // put away before its month strip came: fill it in now
      if (v.querySelector('input[data-sel]:checked')) App.selChanged();
      if (saved.stale) setTimeout(revalidate, 50);
      warmMonths(r, tok, stepped);
    };
    // a tab you've seen comes straight back, like a native tab bar; another month never slides (only its name does)
    if (instant || !dir || inPlace || dir === 'next' || dir === 'prev') return swap();
    const t = vt('page', swap, () => { document.documentElement.dataset.vtdir = dir || ''; });
    if (t) t.kind = 'page';
    return;
  }
  S.feed = null; S.kIdx = -1;
  // Opening a deeper page (an account) keeps the list on screen a little longer, so a quick answer slides straight
  // in; a slow one slides in its placeholder right away and fills it when the data comes.
  const pushing = S.vtDir === 'push' && !instant;
  // Another month: keep this one up (dimmed) a little longer, so the page can redraw in place when the data comes
  const month = (S.vtDir === 'next' || S.vtDir === 'prev') && !instant && morphOk();
  let painted = false;
  // The month's name changes as soon as you press, so the picker answers at once even when the data takes a moment
  if (month && !opts.quiet) stepLabel(v, S.vtDir || '');
  if (month && !opts.quiet) S.dimT = setTimeout(() => { if (tok === S.tok && !painted) v.classList.add('stale'); }, 140);
  // (another month never swaps the page for a placeholder: it stays up, dimmed, and changes in place when the data comes)
  if (!opts.quiet && !month) S.skelT = setTimeout(() => {
    if (tok !== S.tok) return;
    v.classList.remove('stale');
    // a skipped slide still runs its update later, so the placeholder must not land on top of a page that already painted
    const show = () => { if (painted || tok !== S.tok) return; stashPage(prevFeed); v.innerHTML = String(skeleton()); };
    if (!pushing || S.vtDir !== 'push') return show();
    S.vtDir = '';
    const t = vt('page', show, () => { document.documentElement.dataset.vtdir = 'push'; });
    if (t) t.kind = 'page';
  }, pushing ? 350 : month ? 900 : 140);
  const paint: Paint = markup => {
    if (tok !== S.tok) return;
    if (CAP.at >= startedAt && markup && !String(markup).includes('cap-note')) markup = html`${markup}<p class="sub-s cap-note" role="note">${capNote()}</p>`;
    clearTimeout(S.skelT);
    // rows ticked for bulk edit while a quiet redraw of this page was loading: keep them, the next refresh catches up
    if (opts.quiet && key === S.pageKey && S.sel && S.sel.size) return;
    if (S.vtRun && S.vtRun.kind !== 'page') { S.vtRun.finished.catch(() => {}).then(() => paint(markup)); return; }   // let a drawer animation finish first
    const fresh = key !== S.pageKey;
    const from = S.vtFrom && S.vtFrom.node.isConnected && Date.now() - S.vtFrom.t < 4000 ? S.vtFrom.node : null; S.vtFrom = null;
    const dir = S.vtDir; S.vtDir = '';
    // What's on screen now, so a redraw in the same layout can move from it: a refresh or edit of this page, or a
    // month change on a page whose cards don't depend on the month (Overview, Budgets…), which then redraws in place
    // instead of sliding (Transactions too: the title, strip and filters stay, and the new rows fill in from the arrow)
    const snap = morphOk() && S.painted && S.pageLive && !instant && v.firstElementChild && (!fresh || dir === 'next' || dir === 'prev' || (!dir && sameRoute)) ? snapshot(v) : null;
    let frag: DocumentFragment | null = null, inPlace = false;
    if (snap && fresh) {
      const tpl = document.createElement('template'); tpl.innerHTML = String(markup); frag = tpl.content;
      inPlace = sameLayout(snap, frag, dir);
    }
    const swap = () => {
      painted = true; clearTimeout(S.dimT); v.classList.remove('stale');
      const keep = fresh && !inPlace ? 0 : scrollY;
      const lab = monthLabel(v);           // (before the page is put away)
      if (fresh) stashPage(prevFeed);
      if (frag) v.replaceChildren(frag); else v.innerHTML = String(markup);
      keepLabel(v);
      if (!instant) slideLabel(v, lab, dir);
      S.sel = new Set();          // the new rows start unticked
      S.pageKey = key; S.pageHtml = markup; S.pageCacheable = cacheable && !v.querySelector(':scope > .error'); S.pageLive = true;
      if (from) vtName(v.querySelector('.head h1'), 'ptitle');
      if (inPlace) v.classList.remove('enter', 'anim');
      else if (!fresh || !VT_OK || opts.quiet || instant || !dir) { v.classList.remove('enter'); if (fresh && !opts.quiet && !instant) { void v.offsetWidth; v.classList.add('enter'); } }
      scrollTo(0, keep);          // new pages start at the top; a refresh (or a month redrawn in place) keeps your place
      if (snap && (inPlace || !fresh)) morph(v, snap, inPlace ? dir : '');
      if (window.App && App.afterPaint) App.afterPaint(r, { quiet: opts.quiet || !fresh || inPlace });
      if (S.bootStale) { S.bootStale = false; S.warmed = true; setTimeout(() => refreshAll(), 60); }     // opened from saved data: update quietly (and then warm the tabs)
      else if (!S.warmed) { S.warmed = true; warmTabs(); }    // first page after signing in: get the other tabs ready too
    };
    // Only moves with a direction (into an account, back out) slide the page. A plain switch to
    // another page lets its cards rise in instead: snapshotting the whole page for a fade costs the phone a frame or
    // more right as you tap, and the two motions on top of each other look like a stutter.
    if (!fresh || !S.painted || opts.quiet || instant || !dir || inPlace || dir === 'next' || dir === 'prev') { S.painted = true; return swap(); }
    const t = vt('page', swap, () => { document.documentElement.dataset.vtdir = dir || ''; vtName(from, 'ptitle'); });
    if (t) t.kind = 'page';
  };
  paint.live = true; paint.month = month;
  try {
    await (VIEWS[r.name] || VIEWS[''])(r, paint);
    netFails = 0;
    if (S.staleTok === tok && tok === S.tok) setTimeout(revalidate, 50);
    warmMonths(r, tok, stepped);
  } catch (e) {
    if (e instanceof AuthError) return showLogin(e.message);
    const onError = !!v.querySelector(':scope > .error');
    if (opts.quiet && S.pageLive && !onError) { toast('Couldn’t refresh: ' + (e as Error).message); return; }
    if ((e as ApiError).network) {
      paint(html`<div class="error"><strong>Couldn’t load this page.</strong><br>${(e as Error).message}<br>Trying again by itself…<br><br><button class="btn small" data-onclick="App.reload()">Try again now</button></div>`);
      if (tok === S.tok) retryWhenBack(tok);
      return;
    }
    paint(html`<div class="error"><strong>Couldn’t load this page.</strong><br>${(e as Error).message}<br><br><button class="btn small" data-onclick="App.reload()">Try again</button></div>`);
  }
}
// The server couldn't be reached: try the page again after a short wait (longer each time, up to 30 s),
// or straight away when the device comes back online or the tab is shown again. Stops once you go elsewhere.
let netFails = 0, netTimer: ReturnType<typeof setTimeout> | undefined;
function retryWhenBack(tok: number) {
  const go = () => {
    clearTimeout(netTimer); removeEventListener('online', go); document.removeEventListener('visibilitychange', vis);
    if (tok === S.tok) route({ quiet: true });
  };
  const vis = () => { if (document.visibilityState === 'visible') go(); };
  clearTimeout(netTimer);
  netTimer = setTimeout(go, Math.min(30000, 2000 * 2 ** netFails++));
  addEventListener('online', go); document.addEventListener('visibilitychange', vis);
}
// The page on screen was drawn from out-of-date data: fetch what it needs again and redraw only if it changed.
async function revalidate() {
  const r = parseHash(), key = S.pageKey, tok = S.tok; let markup: Html | string | null = null;
  S.swrOff = (S.swrOff || 0) + 1;
  try { await (VIEWS[r.name] || VIEWS[''])(r, h => { markup = h; }); }
  catch (e) { if (e instanceof AuthError) showLogin(e.message); return; }
  finally { S.swrOff--; }
  if (tok === S.tok && key === S.pageKey && markup !== null && markup !== S.pageHtml && !busyHere()) route({ quiet: true });
}
// Something on the page is in use (an open drawer or picker, ticked rows, or something typed into the page itself,
// like a what-if or a budget amount), so a quiet redraw would wipe it
function busyHere() {
  const typed = [...el('view').querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input:not([type=checkbox]):not([type=radio]), textarea')].some(i => i.value !== i.defaultValue);
  return el('drawer').classList.contains('open') || !!(S.sel && S.sel.size) || !el('pickpop').hidden || typed;
}
/* Fetch everything again. visible: show it right away (pull to refresh).
   Otherwise quietly re-fetch the current page and only redraw if something actually changed. */
export async function refreshAll(opts: { visible?: boolean } = {}) {
  if (S.refreshing) return S.refreshing;
  S.refreshing = (async () => {
    S.freshAfter = Date.now(); S.lastRefresh = Date.now(); S.listsAt = 0; PAGES.clear();
    try {
      if (opts.visible) { S.settledAfter = Date.now(); await route({ quiet: true }); return; }
      const r = parseHash(), key = S.pageKey, tok = S.tok;
      let markup: Html | string | null = null;
      try { await (VIEWS[r.name] || VIEWS[''])(r, h => { markup = h; }); }
      catch (e) { if (e instanceof AuthError) showLogin(e.message); return; }
      if (tok === S.tok && key === S.pageKey && markup !== null && markup !== S.pageHtml && !busyHere()) await route({ quiet: true });
    } finally {
      S.refreshing = null;
      warmTabs();
    }
  })();
  return S.refreshing;
}
// Recalculate: throw away every number this app has kept (responses in memory, saved pages, the account/category
// lists, the Categorize history) and redraw from Firefly III.
async function recalcAll(btn?: HTMLButtonElement) {
  if (S.recalcing) return;
  S.recalcing = true;
  const label = btn ? btn.textContent : null; if (btn) { btn.disabled = true; btn.textContent = 'Recalculating…'; }
  try {
    CACHE.clear(); INFLIGHT.clear(); PAGES.clear();
    S.lists = null; S.listsAt = 0; S.inboxHist = null; S.linkTypes = null; S.dirtyAt = 0; S.freshAfter = Date.now(); S.lastRefresh = Date.now();
    await dropSavedCopies();
    await lists(true);
    await route({ quiet: true });
    toast('Recalculated everything from Firefly III');
  } catch (e) {
    if (e instanceof AuthError) return showLogin(e.message);
    toast('Couldn’t recalculate: ' + (e as Error).message);
  } finally {
    S.recalcing = false;
    if (btn && btn.isConnected) { btn.disabled = false; btn.textContent = label; }
  }
}
/* ---------- Loading ahead: other tabs and the months either side, while nothing else is going on ---------- */
// Only while the app is on screen and online, and not when the device asks to save data (Data Saver; iOS doesn't say).
const mayLoadAhead = () => document.visibilityState === 'visible' && navigator.onLine && !S.refreshing &&
  !(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
// Waits for a quiet moment: no requests going, and (where the browser has it) an idle frame. False: give up for now.
async function quiet(stillWanted: () => boolean) {
  for (let i = 0; netBusy() && i < 50; i++) await sleep(200);
  if (netBusy() || !stillWanted() || !mayLoadAhead()) return false;
  const ric = window.requestIdleCallback;
  await new Promise(res => ric ? ric(res, { timeout: 1500 }) : setTimeout(res, 50));
  return stillWanted() && mayLoadAhead();
}
// After a refresh, quietly load the main tabs (this month) so opening them is instant.
const WARM_TABS = ['', 'transactions', 'budgets', 'accounts', 'categories', 'bills', 'piggy', 'forecast'];
async function warmTabs() {
  const cur = parseHash().name, at: [number, number] | undefined = S.all ? undefined : [S.y, S.m];
  await sleep(1200);
  for (const name of WARM_TABS) {
    if (name === cur || !VIEWS[name]) continue;
    if (!await quiet(() => !S.refreshing)) return;
    try { await VIEWS[name]({ name, id: '', q: {}, at }, () => {}); } catch (e) { /* optional */ }
  }
}
// On a page that shows one month, load the month before and the one after, so stepping either way is instant.
// After stepping back it starts with the month before (and after stepping forward, the one after), so going on
// in the same direction is ready first. Stops as soon as you go somewhere else.
const MONTH_PAGES = new Set(['', 'transactions', 'budgets', 'categories', 'bills', 'account']);
async function warmMonths(r: RouteInfo, tok: number, stepped?: string) {
  if (!MONTH_PAGES.has(r.name) || S.all || r.q.search || (r.name === 'account' && r.q.range && r.q.range !== 'month')) return;
  const [y, m] = [S.y, S.m], [ny, nm] = nowMonth();
  await sleep(300);
  for (const d of stepped === 'next' ? [1, -1] : [-1, 1]) {
    const x = new Date(y, m + d, 1), at: [number, number] = [x.getFullYear(), x.getMonth()];
    if ((at[0] - ny) * 12 + at[1] - nm > 12) continue;      // the month picker stops a year ahead
    if (!await quiet(() => tok === S.tok)) return;
    try { await VIEWS[r.name]({ ...r, at }, () => {}); } catch (e) { /* optional */ }
  }
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && !el('app').hidden && S.signedIn && Date.now() - (S.lastRefresh || 0) > 5 * 60 * 1000) refreshAll();
});
// Tapping the tab you're already on scrolls back to the top.
document.addEventListener('click', e => {
  const a = (e.target as HTMLElement).closest && (e.target as HTMLElement).closest('.tabbar a, .nav a'); if (!a) return;
  const h = a.getAttribute('href'), cur = location.hash || '#/';
  if (h === cur || (h === '#/' && cur === '#')) { e.preventDefault(); scrollTo({ top: 0, behavior: 'smooth' }); }
  else if (a.closest('.tabbar') && document.documentElement.classList.contains('shell')) S.tabNav = true;   // app-style: tabs switch instantly
});

// The methods markup handlers call (see events.ts), added to App and to its type
const methods = {
  recalcAll,
};
Object.assign(App, methods);
type Methods = typeof methods;
declare module './base' { interface AppApi extends Methods {} }
